import { IconBell, IconLogout, IconUser } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';
import { useTranslation } from '../../i18n/LocaleProvider';
import type { Locale } from '../../i18n/locale';
import { BrandMark } from '../brand/Logo';
import { Avatar } from '../display/Avatar';
import { Badge } from '../display/Badge';
import { Menu, type MenuEntry } from '../overlay/Menu';
import { LanguageSelect, languageMenuItems } from './LanguageSelect';
import { ThemeSwitcher } from './ThemeSwitcher';

export interface EscrowBuildLanguage {
  /** The interface locale this option selects. */
  value: Locale;
  /** Native language name, including the region when relevant. */
  label: string;
  /** Decorative flag glyph or artwork. The label supplies its accessible name. */
  flag: ReactNode;
}

export interface EscrowBuildHeaderProps extends Omit<ComponentPropsWithRef<'header'>, 'children'> {
  userName: string;
  avatarSrc?: string;
  homeHref?: string;
  languages: readonly EscrowBuildLanguage[];
  language: Locale;
  onLanguageChange: (language: Locale) => void;
  onProfile: () => void;
  onSignOut: () => void;
  /**
   * Supplying it is what makes the header render its own bell, and it drives
   * `aria-expanded` on that bell. A header that shows a notification button
   * with no handler renders a control that looks clickable and does nothing.
   */
  onNotificationsClick?: () => void;
  /** Drives the bell's chip and its accessible name. Zero hides the chip. */
  unreadCount?: number;
  /** Whether the page's notifications panel is open. */
  notificationsOpen?: boolean;
  /** The page's notifications panel, rendered directly under the bar. */
  notificationsPanel?: ReactNode;
}

const CONTROL = cn(
  'relative inline-grid size-11 shrink-0 place-items-center rounded-xs border border-line bg-surface text-heading',
  'transition-colors duration-[var(--dur-fast)] hover:border-primary hover:bg-surface-muted',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);
const MENU = 'max-w-[calc(100vw-2rem)] [&_[role=menuitem]]:min-h-11';

/** Product lockup and account controls; no application navigation or auth side effects. */
export function EscrowBuildHeader({
  userName,
  avatarSrc,
  homeHref = '/',
  languages,
  language,
  onLanguageChange,
  onProfile,
  onSignOut,
  onNotificationsClick,
  unreadCount = 0,
  notificationsOpen,
  notificationsPanel,
  className,
  ...rest
}: EscrowBuildHeaderProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches);
  const [accountOpen, setAccountOpen] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 640px)');
    function onBreakpointChange() {
      setIsMobile(!media.matches);
      setAccountOpen(false);
    }
    media.addEventListener('change', onBreakpointChange);
    onBreakpointChange();
    return () => media.removeEventListener('change', onBreakpointChange);
  }, []);
  const hasUnread = unreadCount > 0;
  /*
     The language rows are built from the locale registry rather than from the
     `languages` prop, because the same list is rendered in two shapes here — as
     a menu of its own from `sm` up, and as the tail of the account menu below
     it — and the two have to carry the same accessible names.
  */
  const languageItems: MenuEntry[] = languageMenuItems(languages, language, onLanguageChange, t);
  const accountItems: MenuEntry[] = [
    { id: 'profile', label: t('ds.header.profile'), icon: <IconUser className="size-4" />, onSelect: onProfile },
    { id: 'account-separator', type: 'separator' },
    { id: 'sign-out', label: t('ds.header.signOut'), icon: <IconLogout className="size-4" />, onSelect: onSignOut },
  ];
  const mobileItems: MenuEntry[] = languageItems.length
    ? [...accountItems, { id: 'language-separator', type: 'separator' }, ...languageItems]
    : accountItems;

  function accountTrigger() {
    return (
      <button type="button" aria-label={t('ds.header.accountNamed', { name: userName })} className={cn(CONTROL, 'border-line-strong shadow-hard-sm')}>
        <Avatar name={userName} src={avatarSrc} shape="square" size="sm" aria-hidden="true" />
      </button>
    );
  }

  return (
    <header {...rest} className={cn('relative z-[var(--z-header)] w-full border-b-2 border-line-strong bg-surface', className)}>
      <div aria-hidden="true" className="pointer-events-none absolute bottom-[-2px] left-3 h-0.5 w-12 bg-primary sm:left-6" />
      <div className="mx-auto flex min-h-16 w-full max-w-[var(--container-wide)] items-center justify-between gap-2 px-3 py-2 sm:min-h-20 sm:gap-6 sm:px-6">
        <a
          href={homeHref}
          aria-label={t('ds.header.home')}
          className="relative inline-flex min-h-11 min-w-0 items-center gap-2 rounded-xs text-heading no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus sm:gap-3"
        >
          <BrandMark size={28} label="" aria-hidden="true" />
          <span className="font-display text-lg font-semibold leading-none tracking-tight sm:text-2xl">escrowbuild.io</span>
        </a>
        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          {languages.length > 0 ? (
            <div className="hidden sm:block">
              {/*
                The trigger is the header's own control shape rather than the
                picker's default — this bar's buttons are all `CONTROL`, and a
                language control that alone sat at a different height would be
                the one row in the bar that does not line up. The menu is the
                picker's, so the width and the flag rows are decided once.
              */}
              <LanguageSelect
                locale={language}
                onLocaleChange={onLanguageChange}
                className={CONTROL}
                placement="bottom-end"
              />
            </div>
          ) : null}
          {/* The state caption is shown wherever there is room for it. Below
              `sm` the control falls back to the bare glyph, which is the only
              thing the 320px header can carry. */}
          <ThemeSwitcher
            showState={!isMobile}
            className={cn('h-11 border border-line text-heading', isMobile ? 'w-11' : 'w-auto')}
          />
          {onNotificationsClick ? (
            // `relative` is the chip's containing block. The chip is centred on
            // this control's top-right corner, which puts 8px of it outside the
            // button; below `sm` the controls are only 4px apart, so the bell
            // takes that 4px back and the chip stops at the account trigger
            // instead of overlapping it (measured at 320px).
            <span className="relative inline-flex shrink-0 max-sm:mr-1">
              <button
                type="button"
                aria-label={
                  hasUnread
                    ? t('ds.header.notificationsUnread', { count: unreadCount })
                    : t('ds.header.notifications')
                }
                aria-haspopup="dialog"
                aria-expanded={notificationsOpen}
                onClick={onNotificationsClick}
                className={CONTROL}
              >
                <IconBell aria-hidden="true" className="size-5" />
              </button>
              {hasUnread ? (
                // Decorative: the count is already in the button's accessible
                // name, so announcing the chip too would read it twice.
                //
                // `grid` and not a bare `block`: as an inline formatting
                // context this box adds a line box around the chip, and the
                // Badge's own `align-middle` then sat the chip 4.6px lower than
                // the offset asked for — measured, not assumed.
                <span aria-hidden="true" className="pointer-events-none absolute -top-2 -right-2 grid">
                  <Badge tone="error" variant="solid" counter>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                </span>
              ) : null}
            </span>
          ) : null}
          <Menu
            label={isMobile ? t('ds.header.accountMenu') : t('ds.header.account')}
            placement="bottom-end"
            items={isMobile ? mobileItems : accountItems}
            open={accountOpen}
            onOpenChange={setAccountOpen}
            className={MENU}
            trigger={accountTrigger()}
          />
        </div>
      </div>
      {/* Anchored beneath the bar, not stacked under it. In normal flow the
          panel grew the header from 82px to 139px and pushed the entire
          document down — opening the bell moved every line of the page the
          reader was looking at. `absolute inset-x-0 top-full` overlays it
          instead, which is what a panel under a bar is. */}
      {notificationsPanel ? (
        <div className="absolute inset-x-0 top-full shadow-md">{notificationsPanel}</div>
      ) : null}
    </header>
  );
}
